/**
 * Skill Mismatch Task API + Worker Skill bulk-edit.
 *
 * Backend:
 *   /hr/skill-mismatch-tasks/*  → HR→GM→MD escalation (mirrors variance)
 *   /hr/worker-skills            → bulk-classify all 159 workers
 */
import apiClient from '@/shared/api/client'

export const SKILL_CLASSES = ['TRAINEE', 'UNSKILLED', 'SEMI_SKILLED', 'SKILLED'] as const
export type SkillClass = (typeof SKILL_CLASSES)[number]

export const MISMATCH_STATUSES = [
  'PENDING_HR', 'ESCALATED_GM', 'ESCALATED_MD', 'RESOLVED', 'WAIVED',
] as const
export type MismatchStatus = (typeof MISMATCH_STATUSES)[number]

export const MISMATCH_SEVERITIES = ['LOW', 'MEDIUM', 'HIGH'] as const
export type MismatchSeverity = (typeof MISMATCH_SEVERITIES)[number]

export const MISMATCH_TYPES = ['OVER_SKILLED', 'UNDER_SKILLED'] as const
export type MismatchType = (typeof MISMATCH_TYPES)[number]

export const MISMATCH_REASON_CODES = [
  'NO_QUALIFIED_AVAILABLE',
  'TRAINING_OPPORTUNITY',
  'PERMANENT_OVERRIDE',
  'WILL_REALLOCATE',
  'SHORT_TERM_COVER',
  'OTHER',
] as const
export type MismatchReasonCode = (typeof MISMATCH_REASON_CODES)[number]

export interface SkillMismatchTask {
  id: string
  allocation_id: string
  employee_id: string
  emp_code: string | null
  emp_name: string | null
  department_name: string | null
  on_date: string | null
  shift_id: string | null
  required_skill_class: SkillClass
  actual_skill_class: SkillClass
  required_role: string | null
  machine_id: string | null
  critical_post_flag: boolean
  mismatch_type: MismatchType
  severity: MismatchSeverity
  status: MismatchStatus
  created_at: string | null
  escalated_to_gm_at: string | null
  escalated_to_md_at: string | null
  resolved_at: string | null
  reason_code: MismatchReasonCode | null
  reason_notes: string | null
  actioned_at: string | null
  gm_acknowledged_at: string | null
  md_acknowledged_at: string | null
}

export interface SkillMismatchSummary {
  pending_hr: number
  escalated_gm: number
  escalated_md: number
  resolved: number
  waived: number
  total_open: number
  by_severity_open: Partial<Record<MismatchSeverity, number>>
  by_type_open: Partial<Record<MismatchType, number>>
}

export interface WorkerSkillRow {
  id: string
  emp_code: string | null
  emp_name: string | null
  department_name: string | null
  designation: string | null
  biometric_id: string | null
  worker_skill_class: SkillClass
  initial_skill_level: string | null
  default_shift_id: string | null
}

const MISMATCH_BASE = '/hr/skill-mismatch-tasks'
const SKILLS_BASE = '/hr/worker-skills'

export const skillMismatchApi = {
  list: async (params: {
    status?: MismatchStatus
    severity?: MismatchSeverity
    mismatch_type?: MismatchType
    employee_id?: string
    on_date_from?: string
    on_date_to?: string
    limit?: number
  } = {}) => (await apiClient.get<SkillMismatchTask[]>(MISMATCH_BASE, { params })).data,

  summary: async () =>
    (await apiClient.get<SkillMismatchSummary>(`${MISMATCH_BASE}/summary`)).data,

  reasonCodes: async () =>
    (await apiClient.get<{ codes: MismatchReasonCode[]; labels: Record<MismatchReasonCode, string> }>(
      `${MISMATCH_BASE}/reason-codes`,
    )).data,

  sweep: async (on_date?: string) =>
    (await apiClient.post<{
      on_date: string
      allocations_checked: number
      created: number
      skipped_no_required_class: number
      skipped_class_matches: number
      skipped_existing_task: number
    }>(`${MISMATCH_BASE}/sweep`, { on_date })).data,

  escalatePending: async () =>
    (await apiClient.post<{
      as_of: string
      promoted_to_gm: number
      promoted_to_md: number
      total_open: number
    }>(`${MISMATCH_BASE}/escalate-pending`, {})).data,

  action: async (task_id: string, body: {
    reason_code: MismatchReasonCode
    reason_notes?: string
    waive?: boolean
  }) => (await apiClient.post<SkillMismatchTask>(`${MISMATCH_BASE}/${task_id}/action`, body)).data,

  gmAcknowledge: async (task_id: string) =>
    (await apiClient.post<SkillMismatchTask>(`${MISMATCH_BASE}/${task_id}/gm-ack`)).data,

  mdAcknowledge: async (task_id: string) =>
    (await apiClient.post<SkillMismatchTask>(`${MISMATCH_BASE}/${task_id}/md-ack`)).data,
}

export const workerSkillsApi = {
  list: async (params: {
    plant_id?: string
    department_id?: string
    skill_class?: SkillClass
    only_active?: boolean
    search?: string
    limit?: number
  } = {}) =>
    (await apiClient.get<WorkerSkillRow[]>(SKILLS_BASE, { params })).data,

  bulkSet: async (rows: { employee_id: string; worker_skill_class: SkillClass }[]) =>
    (await apiClient.post<{
      updated: number
      skipped_invalid_class: number
      skipped_employee_not_found: number
      total_submitted: number
    }>(`${SKILLS_BASE}/bulk-set`, { rows })).data,
}

// ── UI helpers ─────────────────────────────────────────────────────────

export const STATUS_LABEL: Record<MismatchStatus, string> = {
  PENDING_HR: 'Pending HR',
  ESCALATED_GM: 'Escalated to GM',
  ESCALATED_MD: 'Escalated to MD',
  RESOLVED: 'Resolved',
  WAIVED: 'Waived',
}

export const STATUS_COLOR: Record<MismatchStatus, string> = {
  PENDING_HR: 'gold',
  ESCALATED_GM: 'orange',
  ESCALATED_MD: 'red',
  RESOLVED: 'green',
  WAIVED: 'default',
}

export const SEVERITY_COLOR: Record<MismatchSeverity, string> = {
  LOW: 'blue',
  MEDIUM: 'orange',
  HIGH: 'red',
}

export const TYPE_COLOR: Record<MismatchType, string> = {
  OVER_SKILLED: 'gold',
  UNDER_SKILLED: 'red',
}

export const SKILL_CLASS_RANK: Record<SkillClass, number> = {
  TRAINEE: 1,
  UNSKILLED: 2,
  SEMI_SKILLED: 3,
  SKILLED: 4,
}

export const SKILL_CLASS_COLOR: Record<SkillClass, string> = {
  TRAINEE: 'default',
  UNSKILLED: 'blue',
  SEMI_SKILLED: 'cyan',
  SKILLED: 'green',
}
